import { useEffect, useRef } from "react";

/* Animated 3D t-shirt with print — pure SVG + CSS transform layers */
export default function TShirt3D() {
  const wrapRef = useRef(null);

  // Parallax layers on mouse move
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty("--px", x.toFixed(3));
      el.style.setProperty("--py", y.toFixed(3));
    };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <div ref={wrapRef} className="relative w-full h-full perspective-1200 overflow-hidden">
      {/* Ambient glow orbs */}
      <div className="absolute -top-24 -left-16 w-72 h-72 rounded-full bg-[#8B2E2E] blur-3xl opacity-30 animate-pulse-glow" />
      <div className="absolute -bottom-16 -right-16 w-72 h-72 rounded-full bg-[#D4B25C] blur-3xl opacity-20 animate-pulse-glow" style={{ animationDelay: "1.5s" }} />

      {/* Slow rotating conic ring */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-[420px] h-[420px] rounded-full animate-spin-slow"
          style={{
            background: "conic-gradient(from 0deg, transparent 0deg, rgba(212,178,92,0.18) 60deg, transparent 120deg, rgba(139,46,46,0.15) 200deg, transparent 260deg)"
          }}
        />
      </div>

      {/* 3D floating t-shirt */}
      <div
        className="absolute inset-0 flex items-center justify-center animate-float3d"
        style={{ transform: "translate(calc(var(--px,0) * 24px), calc(var(--py,0) * 24px))" }}
      >
        <svg viewBox="0 0 400 500" width="360" height="450" className="drop-shadow-2xl">
          <defs>
            <linearGradient id="body" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#2A231D" />
              <stop offset="55%" stopColor="#0F0B08" />
              <stop offset="100%" stopColor="#1B1611" />
            </linearGradient>
            <linearGradient id="shine" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stopColor="#D4B25C" stopOpacity="0" />
              <stop offset="50%" stopColor="#D4B25C" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#D4B25C" stopOpacity="0" />
            </linearGradient>
            <radialGradient id="print" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#D4B25C" />
              <stop offset="100%" stopColor="#8B2E2E" />
            </radialGradient>
          </defs>
          {/* Tee body */}
          <path
            d="M60 90 L140 40 L180 60 Q200 82 220 60 L260 40 L340 90 L308 178 L280 154 L280 460 L120 460 L120 154 L92 178 Z"
            fill="url(#body)"
            stroke="#D4B25C" strokeOpacity="0.25" strokeWidth="1.5"
          />
          {/* Shine sweep */}
          <path
            d="M60 90 L140 40 L180 60 Q200 82 220 60 L260 40 L340 90 L308 178 L280 154 L280 460 L120 460 L120 154 L92 178 Z"
            fill="url(#shine)"
          />
          {/* Neckline */}
          <path d="M172 55 Q200 90 228 55" fill="none" stroke="#D4B25C" strokeOpacity="0.4" strokeWidth="2" />
          {/* Chest print — Venvay V */}
          <g transform="translate(200 240)">
            <circle r="60" fill="url(#print)" opacity="0.25" />
            <path d="M -32 -30 L 0 30 L 32 -30" fill="none" stroke="#D4B25C" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M -12 -30 L -4 -14" fill="none" stroke="#D4B25C" strokeWidth="4" strokeLinecap="round" />
            <text x="0" y="60" textAnchor="middle" fontFamily="Playfair Display, serif" fontSize="14" letterSpacing="8" fontWeight="900" fill="#D4B25C">VENVAY</text>
          </g>
          {/* Bottom hem stitch */}
          <line x1="120" y1="450" x2="280" y2="450" stroke="#D4B25C" strokeOpacity="0.3" strokeDasharray="4 4" />
        </svg>
      </div>

      {/* Floating spec tags */}
      <div className="absolute top-14 right-10 text-[10px] tracking-[0.3em] uppercase text-[#D4B25C]/80 border border-[#D4B25C]/30 px-3 py-1 backdrop-blur-md bg-black/20"
        style={{ transform: "translate(calc(var(--px,0) * -12px), calc(var(--py,0) * -12px))" }}
      >
        220 GSM · Cotton
      </div>
      <div className="absolute bottom-16 left-6 text-[10px] tracking-[0.3em] uppercase text-[#D4B25C]/80 border border-[#D4B25C]/30 px-3 py-1 backdrop-blur-md bg-black/20"
        style={{ transform: "translate(calc(var(--px,0) * -18px), calc(var(--py,0) * -18px))" }}
      >
        DTG Print · India
      </div>
    </div>
  );
}
